import { Box, Button } from "@mui/material";
import React from "react";
import Select, { components } from "react-select";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Chart from "react-apexcharts";
import "./cost-per-lead.scss";
import { ApexOptions } from "apexcharts";

interface CostPerLeadProps {
  isLoading?: boolean; // Define the type of isLoading if known
  metricsData: any;
  exportChart?: (params: any, data?: any) => void; // Optional exportChart function
  enableExport?: boolean;
}

const CostPerLead: React.FC<CostPerLeadProps> = ({
  isLoading,
  metricsData,
  exportChart,
  enableExport = false, // Default to false if not provided
}) => {
  const [format, setFormat] = React.useState({ value: "PDF", label: "PDF" });
  const campaignsCPL = metricsData?.costPerLeadForCampaigns || [];
  const seriesData: any = [];
  const categories: any = [];
  campaignsCPL.forEach((campaign: any) => {
    const safeValue = campaign?.costPerLead?.replace(/[$,]/g, "") || "0";
    seriesData.push(Number(safeValue));
    categories.push(campaign.name);
  });

  const labelColors: any[] = seriesData?.length
    ? seriesData.map((value: number) => (value === 0 ? "#6488EA" : "#333333"))
    : [];

  const options: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
      },
    },
    noData: {
      text: "No data available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#7B8A8B",
        fontSize: "18px",
        fontFamily: "Roboto, sans-serif",
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        dataLabels: {
          position: "bottom", // Position of data labels
          orientation: "vertical",
        },
        columnWidth: "50%",
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: 5,
      style: {
        colors: labelColors,
        fontWeight: "bold",
      },
      formatter: (val) => `$${val}`,
    },
    xaxis: {
      categories: categories.slice(0, 5) || [],
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: 0,
        trim: true,
        hideOverlappingLabels: false,
        style: {
          fontSize: "12px",
        },
      },
      title: {
        text: "Campaigns",
        style: {
          fontSize: "14px",
          fontWeight: 400,
        },
      },
    },
    yaxis: {
      show: true,
      labels: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      title: {
        text: "Cost Per Lead ",
        style: {
          fontSize: "14px",
          fontWeight: 400,
        },
        offsetX: 15,
      },
    },
    grid: {
      show: false,
    },
    tooltip: {
      enabled: true,
    },
    legend: {
      show: true,
      formatter: function (val, opts) {
        return `$${opts.w.globals.stackedSeriesTotals[opts.seriesIndex]}`;
      },
    },
    colors: ["#6488EA", "#8E44AD", "#1C2F8D", "#BD3D44", "#ED5481"],
  };

  const series = [
    {
      name: "Cost Per Lead",
      data: seriesData.slice(0, 5) || [],
    },
  ];

  const fullSeriesData = [
    {
      name: "Cost Per Lead",
      data: seriesData || [],
    },
  ];

  const fullOptions: ApexOptions = {
    ...options,
    xaxis: {
      ...options.xaxis,
      categories: categories || [],
      labels: {
        style: {
          fontSize: "12px",
        },
      },
    },
    chart: {
      type: "bar",
      id: "costPerLeadForCampaigns",
    },
  };

  const handleExport = () => {
    if (!exportChart) return;

    const params = {
      metrics: "costPerLeadForCampaigns",
      format: format?.value?.toLowerCase(),
    };
    exportChart(params, format.value === "PDF" ? params?.metrics : undefined);
  };
  return (
    <Box className="cpl-wrapper">
      <Box>
        <Box
          sx={{
            padding: "10px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <p className="header-text">CPL (Cost Per Lead)</p>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Select
              options={[
                { value: "PDF", label: "PDF" },
                { value: "CSV", label: "CSV" },
              ]}
              isSearchable={false}
              value={format} // default selected value
              onChange={(selectedOption: any) => setFormat(selectedOption)}
              className="select-dropdown"
              components={{
                IndicatorSeparator: () => null,
                DropdownIndicator: (props) => {
                  return (
                    <components.DropdownIndicator {...props}>
                      <ArrowDropDownIcon
                        style={{ fontSize: 20, color: "black" }}
                      />
                    </components.DropdownIndicator>
                  );
                },
              }}
            />

            <Button
              variant="outlined"
              onClick={handleExport}
              className="address-edit-button"
              disabled={!enableExport}
            >
              Export
            </Button>
          </Box>
        </Box>
        {!isLoading && seriesData?.length ? (
          <div
            style={{
              position: "absolute",
              left: "-9999px",
              top: "-9999px",
              width: "800px",
              height: "400px",
              overflow: "hidden",
            }}
          >
            <Chart
              options={fullOptions}
              series={fullSeriesData}
              type="bar"
              height={400}
            />
          </div>
        ) : (
          <></>
        )}
        <Box sx={{ padding: "0 20px" }}>
          {!isLoading && seriesData?.length ? (
            <Chart options={options} series={series} type="bar" height={250} />
          ) : (
            <>
              <Box className="no-data-found">
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "18px",
                    color: "#7B8A8B",
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 400,
                    margin: "40px 0",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {seriesData?.length ? "Loading..." : " No data available"}
                </p>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CostPerLead;
